import { Icon, Loader, spacing, TextArea } from '@scality/core-ui';
import { Box, Button } from '@scality/core-ui/dist/next';
import { fontSize } from '@scality/core-ui/dist/style/theme';
import React, { useCallback, useContext, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { GuardianContext } from '../GuardianProvider';
import { BackgroundTasksBar } from './BackgroundTasksBar';
import { ChatContextualizer } from './chat-context/ChatContextualizer';
import { SupportPromptGroup } from './SupportPromptGroup';

const SEND_TOOLTIP = { overlay: 'Send message' };

const MessageComposerBox = styled.div<{
  isFloating?: boolean;
  scrollBarWidth?: number;
}>`
  border-radius: ${(props) => (props.isFloating ? spacing.f8 : `${spacing.f8} ${spacing.f8} 0 0`)};
  background-color: ${(props) => props.theme.chatCardGray};
  padding: calc(${spacing.f8} + ${spacing.r8}) 0 ${spacing.r8} calc(${spacing.f8} + ${spacing.r8});
  display: flex;
  flex-direction: column;
  gap: ${spacing.r4};
  box-shadow: 0 -8px 48px rgba(0, 0, 0, 0.5);
  z-index: 1; // for firefox puts the message composer above the chat
  view-transition-name: message-composer;
  /* Same gutters (and inset) as the column above, plus its scrollbar, so both line up. The
     fallback matters: an unresolved var voids the whole declaration, and the margin would
     silently drop to 0. */
  margin-left: calc(var(--chat-gutter) + var(--chat-inset, 0px));
  margin-right: calc(
    var(--chat-gutter) + var(--chat-inset, 0px) + ${({ scrollBarWidth }) => scrollBarWidth}px
  );
  position: relative;
`;

const PromptTextArea = styled(TextArea).attrs({
  placeholder: 'Type your question or request...',
  width: '100%',
  name: 'prompt',
  variant: 'text',
})`
  background: unset;

  border: unset !important;
  overflow: auto;
  resize: none;
  color: white;
  padding-left: 0;
  padding-right: 4rem;
  box-sizing: border-box;
  min-height: 1.5rem;
  max-height: 8rem;
  font-size: ${fontSize.base};
  line-height: 1.5;
  ::placeholder {
    color: ${(props) => props.theme.textSecondary};
    opacity: 1;
    font-style: normal;
    font-size: ${fontSize.base};
  }
`;

const SubmitButton = styled(Button).attrs({
  tooltip: SEND_TOOLTIP,
  variant: 'primary',
})`
  width: 35px;
  position: absolute;
  right: ${spacing.r24};
  top: ${spacing.r12};
  height: 35px;
`;

const Disclaimer = styled.div`
  font-size: ${fontSize.smaller};
  color: ${(props) => props.theme.textSecondary};
  text-align: center;
  padding-right: ${spacing.r4};
`;

/**
 * Measured from `0`, not `auto`: `auto` falls back to the textarea's default two rows, so an
 * empty composer froze at two lines' worth of height.
 */
const resizeTextArea = (element: HTMLTextAreaElement) => {
  element.style.height = '0px';
  element.style.height = `${element.scrollHeight}px`;
};

interface MessageComposerProps {
  onSubmit: (message: string) => void;
  isLoading?: boolean;
  isFloating?: boolean;
  scrollBarWidth?: number;
  /** Show the agent/instance selectors. Off in dashboard mode. */
  showContext?: boolean;
}

export const MessageComposer: React.FC<MessageComposerProps> = ({
  onSubmit,
  isLoading = false,
  isFloating = false,
  scrollBarWidth = 0,
  showContext = true,
}) => {
  const { draftMessage: message, setDraftMessage: setMessage } = useContext(GuardianContext);
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => resizeTextArea(textAreaRef.current!), []);

  const submit = useCallback(() => {
    if (message.trim()) {
      onSubmit(message.trim());
      setMessage('');
      textAreaRef.current?.focus();
      setTimeout(() => {
        if (textAreaRef.current) {
          resizeTextArea(textAreaRef.current);
        }
      });
    }
  }, [message, onSubmit, setMessage]);

  const handleTextAreaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
      resizeTextArea(e.target);
    },
    [setMessage],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
        e.preventDefault();
        submit();
      }
    },
    [submit, isLoading],
  );

  return (
    <MessageComposerBox isFloating={isFloating} scrollBarWidth={scrollBarWidth}>
      <BackgroundTasksBar />
      <Box display="flex" flexDirection="column" gap={0}>
        {showContext && <ChatContextualizer chatInputRef={textAreaRef} />}
        <Box display="flex" alignItems="start" justifyContent="space-between" gap={spacing.r8}>
          <PromptTextArea
            ref={textAreaRef}
            value={message}
            onChange={handleTextAreaChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />

          <SubmitButton
            onClick={submit}
            disabled={!message.trim()}
            isLoading={isLoading}
            icon={isLoading ? <Loader size="large" /> : <Icon name="Arrow-up" size="lg" />}
          />
        </Box>
      </Box>
      {!isFloating && <SupportPromptGroup onSubmit={onSubmit} />}
      <Disclaimer>
        Responses are AI-generated and may contain errors, verify before acting. Your conversations are not used to
        train our models.
      </Disclaimer>
    </MessageComposerBox>
  );
};
